// Local Computer Backup — usa File System Access API (Chrome/Edge/Opera)
// Guarda o handle da pasta escolhida em IndexedDB para persistir entre sessões.

const DB_NAME = "fitpass_local_backup";
const STORE = "handles";
const KEY = "backup_folder";

export function fileSystemAccessSupported() {
  return typeof window !== "undefined" && !!window.showDirectoryPicker;
}

export function isInIframe() {
  try { return window.top !== window.self; }
  catch (e) { return true; }
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key, value) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

async function idbGet(key) {
  const db = await openDB();
  return new Promise((res, rej) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });
}

async function idbDel(key) {
  const db = await openDB();
  return new Promise((res) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => res();
  });
}

export async function pickLocalFolder() {
  if (!fileSystemAccessSupported()) {
    throw new Error("Seu navegador não suporta seleção de pasta local. Use Chrome, Edge ou Opera na versão mais recente.");
  }
  if (isInIframe()) {
    throw new Error("A seleção de pasta não funciona dentro do preview do Emergent. Clique em 'Abrir em nova aba' para rodar o painel em uma guia dedicada e tente novamente.");
  }
  try {
    const handle = await window.showDirectoryPicker({ mode: "readwrite", startIn: "documents" });
    await idbSet(KEY, handle);
    await idbSet("auto_save", false);
    await idbSet("last_saved", null);
    await idbSet("last_filename", null);
    return handle.name;
  } catch (e) {
    if (e?.name === "AbortError") throw e;
    if (e?.name === "SecurityError") {
      throw new Error("Permissão negada pelo navegador. Verifique: 1) está em HTTPS, 2) a página não está em iframe, 3) o botão foi clicado por você (não automático).");
    }
    throw new Error(`${e?.name || "Erro"}: ${e?.message || String(e)}`);
  }
}

export async function getStoredFolder() {
  const handle = await idbGet(KEY);
  const auto_save = (await idbGet("auto_save")) || false;
  const last_saved = (await idbGet("last_saved")) || null;
  const last_filename = (await idbGet("last_filename")) || null;
  return { handle, name: handle?.name || null, auto_save, last_saved, last_filename };
}

export async function clearStoredFolder() {
  await idbDel(KEY);
  await idbDel("auto_save");
  await idbDel("last_saved");
  await idbDel("last_filename");
}

export async function setAutoSave(v) { await idbSet("auto_save", !!v); }

export async function verifyPermission(handle, write = true) {
  const opts = { mode: write ? "readwrite" : "read" };
  if ((await handle.queryPermission(opts)) === "granted") return true;
  return (await handle.requestPermission(opts)) === "granted";
}

export async function saveBlobToFolder(handle, filename, blob) {
  const ok = await verifyPermission(handle, true);
  if (!ok) throw new Error("Permissão negada para gravar na pasta");
  const file = await handle.getFileHandle(filename, { create: true });
  const writable = await file.createWritable();
  await writable.write(blob);
  await writable.close();
  await idbSet("last_saved", new Date().toISOString());
  await idbSet("last_filename", filename);
}

// Baixa o backup mais recente do servidor e grava no handle salvo.
// Retorna { ok, filename, skipped?, error? }
export async function autoSaveLatestBackup(api) {
  const { handle, last_filename } = await getStoredFolder();
  if (!handle) return { ok: false, skipped: true, reason: "sem pasta" };
  try {
    // HEAD-like: busca lista para pegar o filename do mais recente
    const { data } = await api.get("/admin/backup/list");
    const latest = (data.files || [])[0];
    if (!latest) return { ok: false, skipped: true, reason: "sem backup" };
    if (last_filename === latest.filename) return { ok: true, skipped: true, reason: "já salvo" };
    const resp = await api.get("/admin/backup/download-latest", { responseType: "blob" });
    await saveBlobToFolder(handle, latest.filename, resp.data);
    return { ok: true, filename: latest.filename };
  } catch (e) {
    return { ok: false, error: e.message || String(e) };
  }
}
